import {
  closestCorners,
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Check,
  CornerDownRight,
  CornerUpLeft,
  GripVertical,
  ListOrdered,
  Loader,
  Lock,
  Plus,
  TriangleAlert,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import {
  type ClaimDocumentOption,
  type Exhibit,
  findDuplicates,
  findGaps,
  type NestIntent,
  nestIntentOf,
  printedNumber,
  projectDrop,
  renumberSequentially,
  subPiecesOf,
  toExhibit,
  withSequentialOrder,
} from "./exhibit-review-utils";

const BUNDLE = "bundle";
const POOL = "pool";

/**
 * Scroll only when the pointer is pressed against the viewport edge, and gently.
 *
 * dnd-kit's default scrolls as soon as the pointer is within 20% of the top or bottom
 * — on a laptop that is two or three rows at each end — and fast enough to carry the
 * page a whole row per frame. A slot in those bands could not be held: aiming under
 * piece 7 landed under 6 or 8 depending on which way the page was running.
 */
const AUTO_SCROLL = { threshold: { x: 0, y: 0.05 }, acceleration: 4 };

/**
 * The card that follows the cursor while dragging.
 *
 * Without it the source row only fades in place and nothing tracks the pointer, so
 * there is no sense of carrying anything. Rendered in a `DragOverlay` portalled to
 * the body, so the scrolling lists never clip it.
 */
function DragPreviewRow({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="flex cursor-grabbing items-center gap-3 rounded-md border border-blue-300 bg-white px-3 py-2 shadow-lg ring-2 ring-blue-200">
      <GripVertical className="size-4 shrink-0 text-zinc-400" />
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate font-medium text-sm text-zinc-900">{title}</span>
        {subtitle ? <span className="truncate text-xs text-zinc-500">{subtitle}</span> : null}
      </div>
    </div>
  );
}

function SortableExhibitRow({
  exhibit,
  isEditable,
  isDuplicate,
  onRemove,
  onToggleSubPiece,
  onLabelChange,
}: {
  exhibit: Exhibit;
  isEditable: boolean;
  isDuplicate: boolean;
  onRemove: () => void;
  onToggleSubPiece: () => void;
  onLabelChange: (label: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: exhibit.claimDocumentId,
    disabled: !isEditable,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex flex-col gap-1 rounded-md border border-zinc-200 bg-white px-3 py-2",
        exhibit.exhibitSubNumber != null && "ml-6",
        isDuplicate && "border-red-300 bg-red-50",
        isDragging && "border-blue-300 border-dashed bg-blue-50/60 opacity-60",
      )}
    >
      <div className="flex items-center gap-3">
        {isEditable ? (
          <span
            {...attributes}
            {...listeners}
            className="cursor-grab touch-none text-zinc-400 select-none"
            aria-label="Déplacer la pièce"
          >
            <GripVertical className="size-4" />
          </span>
        ) : null}

        <span className="w-24 shrink-0 font-medium text-sm text-zinc-900">
          Pièce n°{printedNumber(exhibit)}
        </span>

        <Input
          size="sm"
          className="min-w-0 flex-1"
          value={exhibit.label ?? ""}
          placeholder="Intitulé au bordereau"
          disabled={!isEditable}
          onChange={(event) => onLabelChange(event.target.value)}
        />

        {isEditable ? (
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              title={
                exhibit.exhibitSubNumber == null
                  ? "Rattacher à la pièce précédente (n°X.Y)"
                  : "Détacher en pièce principale"
              }
              onClick={onToggleSubPiece}
            >
              {exhibit.exhibitSubNumber == null ? <CornerDownRight /> : <CornerUpLeft />}
            </Button>
            <Button variant="ghost" size="sm" title="Retirer du bordereau" onClick={onRemove}>
              <X />
            </Button>
          </div>
        ) : null}
      </div>

      {/*
        On its own line, across the whole row: the filename is how the jurist tells
        two same-labelled pieces apart (two "Réservation confirmée"), and squeezed in
        beside the label it was cut off exactly where the passenger's name starts.
      */}
      <span className={cn("text-xs text-zinc-500 [overflow-wrap:anywhere]", isEditable && "pl-7")}>
        {exhibit.name}
      </span>
    </li>
  );
}

function SortablePoolRow({
  document,
  isEditable,
  onAdd,
}: {
  document: ClaimDocumentOption;
  isEditable: boolean;
  onAdd: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: document.claimDocumentId,
    disabled: !isEditable,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex items-center gap-3 rounded-md border border-zinc-200 border-dashed bg-white px-3 py-2",
        isDragging && "border-blue-300 bg-blue-50/60 opacity-60",
      )}
    >
      {isEditable ? (
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab touch-none text-zinc-400 select-none"
          aria-label="Ajouter au bordereau"
        >
          <GripVertical className="size-4" />
        </span>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm text-zinc-700 [overflow-wrap:anywhere]">{document.name}</span>
        <span className="truncate text-xs text-zinc-400">{document.documentType}</span>
      </div>

      {isEditable ? (
        // Drag is the primary gesture, but it is not keyboard-reachable — this is.
        <Button variant="ghost" size="sm" title="Ajouter au bordereau" onClick={onAdd}>
          <Plus />
        </Button>
      ) : null}
    </li>
  );
}

/**
 * Where the "Enregistrer" button used to be.
 *
 * The list saves itself, so the only thing left to say is whether the jurist can
 * trust what is on screen to be what will be generated.
 */
function SaveStatus({
  isPending,
  isSaving,
  isBlocked,
}: {
  isPending: boolean;
  isSaving: boolean;
  isBlocked: boolean;
}) {
  if (isBlocked) {
    return <span className="text-sm text-red-600">Non enregistré — corrigez les numéros en double</span>;
  }

  if (isSaving) {
    return (
      <span className="flex items-center gap-1.5 text-sm text-zinc-500">
        <Loader className="size-3.5 animate-spin" />
        Enregistrement…
      </span>
    );
  }

  if (isPending) {
    return <span className="text-sm text-zinc-400">Modifications en attente…</span>;
  }

  return (
    <span className="flex items-center gap-1.5 text-sm text-zinc-500">
      <Check className="size-3.5 text-green-600" />
      Enregistré
    </span>
  );
}

interface LegalSubpoenaExhibitsReviewProps {
  exhibits: Exhibit[];
  /** Every exhibit-eligible document on the claim; the pool is whatever is not in the bundle. */
  claimDocuments?: ClaimDocumentOption[];
  isEditable: boolean;
  isSaving: boolean;
  /**
   * False when the API served a proposal rather than what is stored — a pre-TECH-1047
   * bundle it renumbered, say. The list then counts as an unsaved edit, so it is
   * saved like one instead of claiming "Enregistré" over rows that say otherwise.
   */
  isStored?: boolean;
  /**
   * Absent before a project exists — there is no `legal_subpoenas` row to attach an
   * arrangement to, so it travels with the generation call instead of being saved.
   */
  onSave?: (exhibits: Exhibit[]) => void;
  /** Kept in sync so "Générer le projet" sends what is on screen. */
  onChange?: (exhibits: Exhibit[]) => void;
  /**
   * True while an edit is unsaved or in flight. Generation reads the *stored*
   * numbering, so it has to wait for the save to land or it would build from stale
   * numbers.
   */
  onPendingChange?: (isPending: boolean) => void;
}

/**
 * The pre-generation review of the "Liste des pièces" (TECH-1047).
 *
 * Before this existed the numbering was recomputed from scratch on every render path
 * and the jurist saw it for the first time in the generated .docx. Here it is the
 * stored numbering, shown and rearranged before the subpoena is built from it —
 * including dragging documents in from the claim, and back out.
 */
export function LegalSubpoenaExhibitsReview({
  exhibits: serverExhibits,
  claimDocuments = [],
  isEditable,
  isSaving,
  isStored = true,
  onSave,
  onChange,
  onPendingChange,
}: LegalSubpoenaExhibitsReviewProps) {
  const [exhibits, setExhibits] = useState<Exhibit[]>(serverExhibits);
  const [activeId, setActiveId] = useState<string | null>(null);
  // Where the dragged row would land, and whether it has been pulled sideways — together
  // they decide its position and level (see `projectDrop`).
  const [overId, setOverId] = useState<string | null>(null);
  const [nestIntent, setNestIntent] = useState<NestIntent>(0);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // The list on screen, the last server list adopted, and the list last sent to be
  // saved — read by the effect below without making it re-run on every edit.
  const exhibitsRef = useRef(exhibits);
  exhibitsRef.current = exhibits;
  const adoptedRef = useRef(JSON.stringify(serverExhibits));
  const sentRef = useRef<string | null>(null);

  /**
   * Adopt a freshly fetched list — unless the jurist has edited since.
   *
   * A save is followed by a refetch, and react-query refetches on its own too (window
   * focus). Adopting blindly threw away whatever was typed or dragged while that
   * request was in flight. Only a screen that still shows the previous server list,
   * or exactly what was sent to be saved, is replaced; newer edits stay, count as
   * unsaved against the new list, and go out with the next save.
   */
  useEffect(() => {
    const onScreen = JSON.stringify(exhibitsRef.current);

    if (onScreen === adoptedRef.current || onScreen === sentRef.current) {
      setExhibits(serverExhibits);
    }

    adoptedRef.current = JSON.stringify(serverExhibits);
    sentRef.current = null;
  }, [serverExhibits]);

  const duplicates = useMemo(() => findDuplicates(exhibits), [exhibits]);
  const gaps = useMemo(() => findGaps(exhibits), [exhibits]);
  // Only a proposal that can be saved counts: before the first generation there is
  // no row to save it to, and the arrangement travels with the generation instead.
  const isUnsavedProposal = !isStored && onSave != null;
  const isDirty = useMemo(
    () => isUnsavedProposal || JSON.stringify(exhibits) !== JSON.stringify(serverExhibits),
    [isUnsavedProposal, exhibits, serverExhibits],
  );

  // Derived, not held in state, so removing from the bundle puts the document back
  // in the pool without a second source of truth to keep in step.
  const pool = useMemo(() => {
    const inBundle = new Set(exhibits.map((exhibit) => exhibit.claimDocumentId));
    return claimDocuments.filter((document) => !inBundle.has(document.claimDocumentId));
  }, [claimDocuments, exhibits]);

  // A dragged piece carries its sub-pieces: they are folded away for the drag and put
  // back under it on drop.
  const carriedIds = useMemo(
    () => new Set(activeId ? subPiecesOf(exhibits, activeId).map((exhibit) => exhibit.claimDocumentId) : []),
    [activeId, exhibits],
  );
  const visibleExhibits = exhibits.filter((exhibit) => !carriedIds.has(exhibit.claimDocumentId));

  // What the list would become if the row were dropped now — every row shows its
  // projected number, and the dragged one its projected level, before letting go.
  const projection = useMemo(
    () =>
      activeId && exhibits.some((exhibit) => exhibit.claimDocumentId === activeId)
        ? projectDrop(exhibits, activeId, overId, nestIntent)
        : null,
    [activeId, exhibits, overId, nestIntent],
  );
  const projectedById = useMemo(
    () =>
      projection
        ? new Map(
            renumberSequentially(projection.exhibits).map((exhibit) => [exhibit.claimDocumentId, exhibit]),
          )
        : null,
    [projection],
  );

  const { setNodeRef: setBundleRef, isOver: isOverBundle } = useDroppable({ id: BUNDLE });
  const { setNodeRef: setPoolRef, isOver: isOverPool } = useDroppable({ id: POOL });

  // Held in a ref so the debounce below does not depend on the callback's identity:
  // the parent passes an inline arrow, which would reset the timer on every render
  // and mean the save never fires.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const isPending = isDirty || isSaving;

  useEffect(() => {
    onPendingChange?.(isPending);
  }, [isPending, onPendingChange]);

  /**
   * Save shortly after the last edit.
   *
   * Debounced rather than saved per change because a label is typed a character at a
   * time and a drag fires as the pointer moves — each save replaces every row of the
   * bundle. Duplicates are excluded because the API rejects them, and the warning
   * above tells the jurist why nothing is being saved. Nothing is saved mid-drag
   * either: a document from the pool joins the list as soon as it hovers it, and a
   * slow drag would otherwise persist wherever it happened to be passing.
   */
  useEffect(() => {
    if (!isEditable || !isDirty || duplicates.length > 0 || activeId !== null || !onSaveRef.current) {
      return;
    }

    // A second of calm after the last drag or keystroke: long enough not to save
    // mid-word, short enough that "Régénérer" is not left waiting.
    const timer = setTimeout(() => {
      sentRef.current = JSON.stringify(exhibits);
      onSaveRef.current?.(exhibits);
    }, 1000);

    return () => clearTimeout(timer);
  }, [exhibits, isDirty, isEditable, duplicates.length, activeId]);

  /**
   * Every edit renumbers the list and schedules a save.
   *
   * Renumbering used to be a button, on the grounds that the subpoena's prose cites
   * fixed piece numbers — but those citations resolve through the stored numbering
   * now (`citedPieceNumber`), so they follow a renumbering instead of being broken by
   * it. Dropping a document in position 3 therefore makes it piece 3, as you would
   * expect, and the numbering is contiguous by construction.
   */
  function update(next: Exhibit[]) {
    const renumbered = withSequentialOrder(renumberSequentially(next));
    setExhibits(renumbered);
    onChange?.(renumbered);
  }

  function addToBundle(claimDocumentId: string, atIndex?: number) {
    const document = pool.find((candidate) => candidate.claimDocumentId === claimDocumentId);

    if (!document) {
      return;
    }

    const next = [...exhibits];
    next.splice(atIndex ?? next.length, 0, toExhibit(document, exhibits));
    update(next);
  }

  function containerOf(id: string): typeof BUNDLE | typeof POOL | null {
    if (id === BUNDLE || id === POOL) {
      return id;
    }
    if (exhibits.some((exhibit) => exhibit.claimDocumentId === id)) {
      return BUNDLE;
    }
    return pool.some((document) => document.claimDocumentId === id) ? POOL : null;
  }

  /**
   * Move the row across lists *during* the drag, so the gap opens where it will land.
   *
   * Cross-container sorting is the one thing dnd-kit does not do for you: without
   * this the row stays put until you let go and there is no way to tell where it is
   * going. Once it has crossed, `from === to` and this returns immediately, so the
   * pointer can wander without thrashing the list.
   */
  function handleDragOver({ active, over }: DragOverEvent) {
    setOverId(over ? String(over.id) : null);

    if (!over) {
      return;
    }

    const draggedId = String(active.id);
    const targetId = String(over.id);
    const from = containerOf(draggedId);
    const to = containerOf(targetId);

    if (!from || !to || from === to) {
      return;
    }

    if (from === POOL && to === BUNDLE) {
      const overIndex = exhibits.findIndex((exhibit) => exhibit.claimDocumentId === targetId);
      addToBundle(draggedId, overIndex === -1 ? undefined : overIndex);
      return;
    }

    // Out of the bordereau, sub-pieces included: they travel with their piece.
    update(
      exhibits.filter(
        (exhibit) => exhibit.claimDocumentId !== draggedId && !carriedIds.has(exhibit.claimDocumentId),
      ),
    );
  }

  /** Only re-render when the sideways intent changes, not on every pixel. */
  function handleDragMove({ delta }: DragMoveEvent) {
    const intent = nestIntentOf(delta.x);
    if (intent !== nestIntent) {
      setNestIntent(intent);
    }
  }

  function resetDrag() {
    setActiveId(null);
    setOverId(null);
    setNestIntent(0);
  }

  function handleDragEnd({ active, over, delta }: DragEndEvent) {
    resetDrag();

    if (!over) {
      return;
    }

    const draggedId = String(active.id);
    const targetId = String(over.id);

    // Crossing lists already happened in `handleDragOver`; what is left is the final
    // position and level within the bordereau — which can change without moving at
    // all, by dragging a row sideways in place.
    if (containerOf(draggedId) !== BUNDLE || containerOf(targetId) === POOL) {
      return;
    }

    update(projectDrop(exhibits, draggedId, targetId, nestIntentOf(delta.x)).exhibits);
  }

  /** What rides under the cursor — the bundle row if it has crossed, else the pool row. */
  const activeExhibit = activeId ? (projectedById?.get(activeId) ?? null) : null;
  const activeParentNumber = activeExhibit?.exhibitSubNumber != null ? activeExhibit.exhibitNumber : null;
  const activePoolDocument = pool.find((document) => document.claimDocumentId === activeId);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      autoScroll={AUTO_SCROLL}
      onDragStart={({ active }: DragStartEvent) => {
        setActiveId(String(active.id));
        setOverId(String(active.id));
        setNestIntent(0);
      }}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={resetDrag}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-medium text-sm text-zinc-700">
            <ListOrdered className="size-4" />
            Liste des pièces ({exhibits.length})
          </div>

          {isEditable ? (
            <SaveStatus isPending={isPending} isSaving={isSaving} isBlocked={duplicates.length > 0} />
          ) : (
            <div className="flex items-center gap-1.5 text-sm text-zinc-500">
              <Lock className="size-3.5" />
              Assignation signifiée — numérotation figée
            </div>
          )}
        </div>

        {duplicates.length > 0 ? (
          <Alert variant="error">
            <TriangleAlert />
            <AlertTitle>Numéros en double</AlertTitle>
            <AlertDescription>
              {duplicates.map((number) => `Pièce n°${number}`).join(", ")} — corrigez avant d'enregistrer.
            </AlertDescription>
          </Alert>
        ) : null}

        {gaps.length > 0 ? (
          <Alert>
            <TriangleAlert />
            <AlertTitle>Numérotation non continue</AlertTitle>
            <AlertDescription>
              {gaps.map((number) => `n°${number}`).join(", ")} manquant{gaps.length > 1 ? "s" : ""}.
            </AlertDescription>
          </Alert>
        ) : null}

        <SortableContext
          items={visibleExhibits.map((exhibit) => exhibit.claimDocumentId)}
          strategy={verticalListSortingStrategy}
        >
          <ul
            ref={setBundleRef}
            className={cn(
              "flex min-h-16 flex-col gap-1 rounded-md p-1 transition-colors",
              isOverBundle && "bg-blue-50 ring-2 ring-blue-200",
            )}
          >
            {exhibits.length === 0 ? (
              <li className="px-3 py-4 text-center text-sm text-zinc-400">
                Glissez des documents ici pour composer le bordereau.
              </li>
            ) : null}
            {visibleExhibits.map((exhibit) => (
              <SortableExhibitRow
                key={exhibit.claimDocumentId}
                // Mid-drag, the number and level it would have if dropped now.
                exhibit={projectedById?.get(exhibit.claimDocumentId) ?? exhibit}
                isEditable={isEditable}
                isDuplicate={duplicates.includes(printedNumber(exhibit))}
                onRemove={() =>
                  update(exhibits.filter((item) => item.claimDocumentId !== exhibit.claimDocumentId))
                }
                onToggleSubPiece={() =>
                  update(
                    exhibits.map((item) =>
                      item.claimDocumentId === exhibit.claimDocumentId
                        ? { ...item, exhibitSubNumber: item.exhibitSubNumber == null ? 1 : null }
                        : item,
                    ),
                  )
                }
                onLabelChange={(label) =>
                  update(
                    exhibits.map((item) =>
                      item.claimDocumentId === exhibit.claimDocumentId ? { ...item, label } : item,
                    ),
                  )
                }
              />
            ))}
          </ul>
        </SortableContext>

        {claimDocuments.length > 0 ? (
          <>
            <div className="font-medium text-sm text-zinc-700">
              Documents du dossier hors bordereau ({pool.length})
            </div>

            <SortableContext
              items={pool.map((document) => document.claimDocumentId)}
              strategy={verticalListSortingStrategy}
            >
              <ul
                ref={setPoolRef}
                className={cn(
                  "flex min-h-16 flex-col gap-1 rounded-md bg-zinc-50 p-1 transition-colors",
                  isOverPool && "bg-red-50 ring-2 ring-red-200",
                )}
              >
                {pool.length === 0 ? (
                  <li className="px-3 py-4 text-center text-sm text-zinc-400">
                    Tous les documents du dossier sont au bordereau.
                  </li>
                ) : null}
                {pool.map((document) => (
                  <SortablePoolRow
                    key={document.claimDocumentId}
                    document={document}
                    isEditable={isEditable}
                    onAdd={() => addToBundle(document.claimDocumentId)}
                  />
                ))}
              </ul>
            </SortableContext>
          </>
        ) : null}
      </div>

      {/*
        Portalled: the review lives in a dialog whose popup has `will-change: transform`,
        which makes the overlay's `position: fixed` relative to the popup. The overlay —
        and the rect dnd-kit measures collisions from — then sat ~250px right of the
        pointer, where the indented sub-pieces won every collision: rows flickered around
        them and the slot next to a sub-piece could not be held.
      */}
      {createPortal(
        <DragOverlay dropAnimation={null}>
          {activeExhibit ? (
            <DragPreviewRow
              title={`Pièce n°${printedNumber(activeExhibit)} ${activeExhibit.label ?? ""}`.trim()}
              subtitle={
                activeParentNumber !== null
                  ? `↳ Rattachée à la pièce n°${activeParentNumber} — glisser à gauche pour détacher`
                  : carriedIds.size > 0
                    ? `Avec ${carriedIds.size} sous-pièce${carriedIds.size > 1 ? "s" : ""}`
                    : "Glisser à droite pour rattacher à la pièce au-dessus"
              }
            />
          ) : activePoolDocument ? (
            <DragPreviewRow title={activePoolDocument.name} subtitle={activePoolDocument.documentType} />
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}
